import { useState, useEffect, useMemo } from 'react';
import { supabase, Restaurant, MenuItem, GroceryStore } from '../lib/supabase';
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  Store,
  UtensilsCrossed,
  MessageSquare,
  Trash2,
  Check,
} from 'lucide-react';

type SizeOption = 'liten' | 'medium' | 'stor';
type MenuItemPrice = { size: SizeOption; price: number };

type MenuItemWithPrices = MenuItem & {
  menu_item_prices?: MenuItemPrice[];
};

type CartItem = {
  menuItem: MenuItemWithPrices;
  size: SizeOption;
  unitPrice: number;
  quantity: number;
};

type Category = 'restaurant' | 'dagligvarer';

export default function BestillPage() {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [groceryStores, setGroceryStores] = useState<GroceryStore[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemWithPrices[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);

  // valgt størrelse per item-id
  const [selectedSizes, setSelectedSizes] = useState<Record<string, SizeOption>>({});
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Dagligvarer: SMS-bygging
  const [gItem, setGItem] = useState('');
  const [gItems, setGItems] = useState<string[]>([]);
  const [gReady, setGReady] = useState(false);
  const smsNumber = '+4790976011';
  const smsHeader = 'Hei Gjesdalivery, dette er min handleliste';

  const smsBody = useMemo(() => {
    const list = gItems.length ? '\n\n' + gItems.join('\n') : '';
    return `${smsHeader}${list}`;
  }, [gItems]);

  const smsHref = useMemo(() => {
    const ua = navigator.userAgent || '';
    const isApple = /iPad|iPhone|iPod|Macintosh/.test(ua);
    const encoded = encodeURIComponent(smsBody);
    return isApple
      ? `sms:${smsNumber}&body=${encoded}`
      : `sms:${smsNumber}?body=${encoded}`;
  }, [smsBody]);

  // ---------- Data ----------
  useEffect(() => {
    if (selectedCategory === 'restaurant') loadRestaurants();
    if (selectedCategory === 'dagligvarer') loadGroceryStores();
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedRestaurant) loadMenuItems(selectedRestaurant);
  }, [selectedRestaurant]);

  const loadRestaurants = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('restaurants').select('*').eq('is_active', true);
    if (!error) setRestaurants(data || []);
    setLoading(false);
  };

  const loadGroceryStores = async () => {
    // Beholder kall i tilfelle du vil vise butikker senere, men UIet bruker SMS-flyt
    const { data } = await supabase.from('grocery_stores').select('*').eq('is_active', true);
    setGroceryStores(data || []);
  };

  const loadMenuItems = async (restaurantId: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('menu_items')
      .select('*, menu_item_prices(size, price)')
      .eq('restaurant_id', restaurantId)
      .eq('is_available', true);

    if (error) {
      setMenuItems([]);
      setSelectedSizes({});
      setLoading(false);
      return;
    }

    const items = (data as MenuItemWithPrices[]) || [];
    setMenuItems(items);

    // default size per item
    const defaults: Record<string, SizeOption> = {};
    for (const it of items) {
      const sizes = (it.menu_item_prices || []).map(s => s.size);
      if (sizes.length === 0) {
        defaults[it.id] = 'medium';
      } else {
        defaults[it.id] = (sizes.includes('medium') ? 'medium' : sizes[0]) as SizeOption;
      }
    }
    setSelectedSizes(defaults);
    setLoading(false);
  };

  // ---------- Helpers ----------
  const priceFor = (item: MenuItemWithPrices, size: SizeOption): number => {
    const p = item.menu_item_prices?.find(m => m.size === size)?.price;
    return typeof p === 'number' ? p : item.price;
  };

  const addToCart = (item: MenuItemWithPrices) => {
    const size = selectedSizes[item.id] ?? 'medium';
    const unitPrice = priceFor(item, size);
    const key = `${item.id}-${size}`;
    const existingIndex = cart.findIndex(ci => `${ci.menuItem.id}-${ci.size}` === key);

    if (existingIndex >= 0) {
      const next = [...cart];
      next[existingIndex] = { ...next[existingIndex], quantity: next[existingIndex].quantity + 1 };
      setCart(next);
    } else {
      setCart(prev => [...prev, { menuItem: item, size, unitPrice, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemId: string, size: SizeOption, change: number) => {
    const key = `${itemId}-${size}`;
    setCart(prev =>
      prev
        .map(ci =>
          `${ci.menuItem.id}-${ci.size}` === key
            ? { ...ci, quantity: Math.max(0, ci.quantity + change) }
            : ci
        )
        .filter(ci => ci.quantity > 0)
    );
  };

  const removeFromCart = (itemId: string, size: SizeOption) => {
    setCart(prev => prev.filter(ci => !(ci.menuItem.id === itemId && ci.size === size)));
  };

  const total = cart.reduce((sum, ci) => sum + ci.unitPrice * ci.quantity, 0);
  // 1 % av totalen = VærCoins
  const vaerCoins = total * 0.01;

  // ---------- UI STATES ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 dark:text-gray-400">
        Laster...
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16 transition-colors">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Hva vil du bestille?</h1>
          <div className="grid md:grid-cols-2 gap-8">
            <button
              onClick={() => setSelectedCategory('restaurant')}
              className="p-8 bg-white dark:bg-gray-800 rounded-xl shadow hover:shadow-lg transition"
            >
              <UtensilsCrossed className="w-10 h-10 text-[#004D99] dark:text-orange-400 mx-auto mb-3" />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Restaurant</h2>
            </button>
            <button
              onClick={() => setSelectedCategory('dagligvarer')}
              className="p-8 bg-white dark:bg-gray-800 rounded-xl shadow hover:shadow-lg transition"
            >
              <Store className="w-10 h-10 text-orange-500 dark:text-orange-400 mx-auto mb-3" />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Dagligvarer</h2>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- DAGLIGVARER: SMS-FLYT ----------
  if (selectedCategory === 'dagligvarer') {
    function addGItem() {
      const clean = gItem.trim();
      if (!clean) return;
      setGItems(prev => [...prev, clean]);
      setGItem('');
    }

    function removeGItem(idx: number) {
      setGItems(prev => prev.filter((_, i) => i !== idx));
    }

    function markReady() {
      if (!gItems.length) return;
      setGReady(true);
    }

    function editAgain() {
      setGReady(false);
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => setSelectedCategory(null)}
            className="text-[#004D99] dark:text-orange-400 mb-6 hover:underline"
          >
            ← Tilbake
          </button>

          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Dagligvarer</h1>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Venstre: bygg meldingen */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Lag handlelista og gjør klar SMS
                </h2>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Skriv hva du vil ha, trykk <strong>+</strong> for å legge til flere, trykk{' '}
                <strong>Ferdig</strong> for å generere SMS. Deretter trykk <strong>Send SMS</strong> for å åpne
                meldingsappen.
              </p>

              <div className="flex gap-2 mb-4">
                <input
                  value={gItem}
                  onChange={(e) => setGItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addGItem()}
                  className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="Skriv vare (f.eks. 4x 1,5L Pepsi Max)"
                  aria-label="Legg til vare"
                />
                <button
                  onClick={addGItem}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white px-4 py-3 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Legg til
                </button>
              </div>

              <ul className="space-y-2 mb-6">
                {gItems.map((v, idx) => (
                  <li
                    key={`${v}-${idx}`}
                    className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3"
                  >
                    <span className="text-gray-900 dark:text-gray-100">{v}</span>
                    <button
                      onClick={() => removeGItem(idx)}
                      className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                      aria-label={`Fjern ${v}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-3">
                {!gReady ? (
                  <button
                    onClick={markReady}
                    disabled={!gItems.length}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 disabled:bg-emerald-600/40 hover:bg-emerald-700 text-white px-5 py-3 font-semibold"
                  >
                    <Check className="w-4 h-4" />
                    Ferdig
                  </button>
                ) : (
                  <>
                    <a
                      href={smsHref}
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white px-5 py-3 font-semibold"
                    >
                      Send SMS
                    </a>
                    <button
                      onClick={editAgain}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 px-5 py-3 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      Rediger
                    </button>
                  </>
                )}
                <span className="text-xs text-gray-500">Forhåndsvisning vises til høyre.</span>
              </div>
            </section>

            {/* Høyre: chat-aktig eksempel/preview */}
            <aside className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Eksempel på handleliste (SMS)
              </h3>

              <div className="rounded-2xl bg-gray-50 dark:bg-gray-950 p-4 shadow-sm border border-gray-200 dark:border-gray-800">
                {/* Avsender-bobler */}
                <div className="max-w-[85%] ml-auto space-y-2">
                  <Bubble text={smsHeader} align="right" tone="blue" />
                  {gItems.length ? (
                    gItems.map((v, i) => <Bubble key={i} text={v} align="right" tone="blue" />)
                  ) : (
                    <>
                      <Bubble text="4x 1,5L Pepsi Max" align="right" tone="blue" />
                      <Bubble text="Kylling 1 kg (helst Coop)" align="right" tone="blue" />
                      <Bubble text="6–7 kilo jasminris" align="right" tone="blue" />
                      <Bubble text="2 stk Monster Mango Loco" align="right" tone="blue" />
                      <Bubble text="Det var det :)" align="right" tone="blue" />
                    </>
                  )}
                </div>

                {/* Mottatt-boble */}
                <div className="max-w-[70%] mt-3">
                  <Bubble text="Mottatt." align="left" tone="gray" />
                </div>
              </div>

              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Melding som sendes
                </p>
                <pre className="whitespace-pre-wrap text-sm bg-white dark:bg-black/30 border border-gray-200 dark:border-gray-800 rounded-xl p-3 text-gray-800 dark:text-gray-100">
{smsBody}
                </pre>
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  // ---------- RESTAURANT: velg restaurant ----------
  if (!selectedRestaurant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => setSelectedCategory(null)}
            className="text-[#004D99] dark:text-orange-400 mb-6 hover:underline"
          >
            ← Tilbake
          </button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Restauranter</h1>

          <div className="grid md:grid-cols-3 gap-6">
            {restaurants.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedRestaurant(r.id)}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow hover:shadow-lg text-left transition"
              >
                <div className="flex items-center gap-4 mb-3">
                  {r.logo_url ? (
                    <img
                      src={r.logo_url}
                      alt={r.name}
                      className="w-16 h-16 object-contain rounded"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <UtensilsCrossed className="w-8 h-8 text-[#004D99] dark:text-orange-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{r.name}</h3>
                    {r.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{r.description}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 w-full py-2.5 text-center bg-[#004D99] hover:bg-[#003366] text-white font-semibold rounded-lg transition-colors">
                  Se meny
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- RESTAURANT: meny ----------
  const grouped = menuItems.reduce((acc, item) => {
    const cat = item.category || 'Annet';
    (acc[cat] ||= []).push(item);
    return acc;
  }, {} as Record<string, MenuItemWithPrices[]>);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors">
      <div className="max-w-7xl mx-auto px-4">
        <button
          onClick={() => {
            setSelectedRestaurant(null);
            setMenuItems([]);
          }}
          className="text-[#004D99] dark:text-orange-400 mb-6 hover:underline"
        >
          ← Tilbake
        </button>

        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
          {restaurants.find(r => r.id === selectedRestaurant)?.name}
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Venstre: meny */}
          <div className="lg:col-span-2 space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{cat}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {items.map(item => {
                    const size = selectedSizes[item.id] ?? 'medium';
                    const sizes = item.menu_item_prices?.map(s => s.size) || [];
                    const price = priceFor(item, size);
                    const hasVariants = item.menu_item_prices && item.menu_item_prices.length > 1;

                    return (
                      <div
                        key={item.id}
                        className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow hover:shadow-lg transition"
                      >
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.name}</h3>
                        {item.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{item.description}</p>
                        )}

                        {hasVariants && (
                          <div className="mb-3">
                            <label className="sr-only">Størrelse</label>
                            <select
                              value={size}
                              onChange={e =>
                                setSelectedSizes(prev => ({
                                  ...prev,
                                  [item.id]: e.target.value as SizeOption
                                }))
                              }
                              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                            >
                              {sizes.map(s => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-[#004D99] dark:text-orange-400">
                            {price} kr
                          </span>
                          <button
                            onClick={() => addToCart(item)}
                            className="px-4 py-2 bg-[#004D99] hover:bg-[#003366] text-white rounded-lg"
                          >
                            Legg til
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Høyre: handlekurv */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Handlekurv</h2>
                <span className="bg-[#004D99] text-white px-3 py-1 rounded-full text-sm">
                  {cart.reduce((s, c) => s + c.quantity, 0)}
                </span>
              </div>

              {cart.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">Handlekurven er tom</p>
              ) : (
                <>
                  <div className="space-y-4 mb-4">
                    {cart.map(ci => (
                      <div
                        key={`${ci.menuItem.id}-${ci.size}`}
                        className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {ci.menuItem.name}
                            {ci.menuItem.menu_item_prices && ci.menu_item_prices.length > 1 ? (
                              <span className="text-xs text-gray-500"> ({ci.size})</span>
                            ) : null}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{ci.unitPrice} kr</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(ci.menuItem.id, ci.size, -1)}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-6 text-center text-gray-900 dark:text-white">{ci.quantity}</span>
                          <button
                            onClick={() => updateQuantity(ci.menuItem.id, ci.size, 1)}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(ci.menuItem.id, ci.size)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between font-semibold text-gray-900 dark:text-white">
                    <span>Totalt</span>
                    <span>{total.toFixed(2)} kr</span>
                  </div>

                  <div className="mt-1 flex justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span>VærCoins (1%)</span>
                    <span className="font-medium">{vaerCoins.toFixed(2)}</span>
                  </div>

                  <button className="w-full mt-4 py-3 bg-[#004D99] hover:bg-[#003366] text-white rounded-lg">
                    Gå til kassen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Enkel chat-boble med runde hjørner og liten skygge */
function Bubble({
  text,
  align = 'right',
  tone = 'blue',
}: {
  text: string;
  align?: 'left' | 'right';
  tone?: 'blue' | 'gray';
}) {
  const isRight = align === 'right';
  const base = 'px-4 py-2 rounded-2xl shadow-sm text-sm leading-snug inline-block';
  const color =
    tone === 'blue'
      ? 'bg-[#3B82F6] text-white'
      : 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100';
  const radius = isRight ? 'rounded-tr-sm' : 'rounded-tl-sm';
  const justify = isRight ? 'justify-end' : 'justify-start';
  return (
    <div className={`flex ${justify}`}>
      <span className={`${base} ${color} ${radius}`}>{text}</span>
    </div>
  );
}
